import { useCallback, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { SOCKET_EVENTS, type TeamsActivityEvent } from '@atlas/shared';
import { useSocket } from '@/contexts/SocketContext';

/**
 * How long to sit on a notification before refetching.
 *
 * Graph fans out one notification per changed message and retries anything we
 * do not acknowledge fast enough, so a single busy conversation easily produces
 * a handful within a second. Each one would otherwise become its own Graph
 * round-trip against a per-user throttle, for a panel that can only display the
 * final state anyway. A trailing window collapses the burst into one refetch.
 */
const ACTIVITY_DEBOUNCE_MS = 1_000;

interface UseTeamsRealtimeOptions {
  /**
   * True only when the panel is actually being looked at. Governs the message
   * thread: an off-screen thread is marked stale rather than refetched, since
   * nothing is rendering it.
   */
  isViewing: boolean;
  /**
   * True when Teams is usable at all — configured, connected, not awaiting a
   * reconnect.
   *
   * The chat list refetches on this rather than on `isViewing`, which is a
   * deliberate reversal of the original design ("a notification for a closed
   * drawer never costs a Graph call"). The unread badge lives in the AppBar and
   * has to be able to light up with the panel shut, and the only way to know it
   * should is to ask. The cost is bounded and worth naming: one debounced
   * `/me/chats` call per burst of notifications, per connected user, and only
   * when Graph actually tells us something changed — closed panels still never
   * poll on a timer.
   */
  isConnected: boolean;
  /** The open thread, or null when the list is showing. */
  activeChatId: string | null;
  /**
   * True while a send is in flight. A refetch replaces the whole page set, so
   * one landing between the optimistic insert and the send's own settle
   * invalidation would drop the echo and make a successful send look failed —
   * exactly the race `refetchInterval: !isSending` guards polling against.
   */
  isSending: boolean;
}

/**
 * Push-based refresh for the Teams sidebar.
 *
 * The backend turns a Microsoft Graph change notification into a
 * `teams:activity` event on the user's socket room. The event deliberately
 * carries no message content — it is a bare "something changed", so everything
 * the user sees still arrives through the normal authenticated, delegated API.
 * This hook therefore only ever invalidates queries; it must never render from
 * the payload.
 *
 * Reuses the app-wide socket from `SocketContext` (same connection the activity
 * feed and messaging use) — there is no second connection and no hand-rolled
 * reconnect logic here.
 */
export function useTeamsRealtime({
  isViewing,
  isConnected,
  activeChatId,
  isSending,
}: UseTeamsRealtimeOptions): void {
  const { socket } = useSocket();
  const queryClient = useQueryClient();

  // Read through refs inside the debounced flush: re-subscribing on every
  // keystroke-scale change (isSending flips twice per send) would drop pending
  // timers and re-register the listener for no benefit.
  const isViewingRef = useRef(isViewing);
  const isConnectedRef = useRef(isConnected);
  const activeChatIdRef = useRef(activeChatId);
  const isSendingRef = useRef(isSending);
  isViewingRef.current = isViewing;
  isConnectedRef.current = isConnected;
  activeChatIdRef.current = activeChatId;
  isSendingRef.current = isSending;

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Set when a notification named the open thread, or named nothing at all. */
  const wantsMessagesRef = useRef(false);
  const wantsChatsRef = useRef(false);

  const flush = useCallback(() => {
    timerRef.current = null;

    // Off-screen thread: mark the cache stale without refetching. React Query
    // then fetches once on the next mount/enable, so reopening the panel is
    // still fresh and a background notification costs nothing for messages.
    const refetchType = isViewingRef.current ? undefined : ('none' as const);

    // The chat list is the exception, and the reason is the unread badge: it
    // refetches whenever the connection is alive, open drawer or not, because a
    // badge nobody asked Graph about can never appear. A dead connection still
    // only marks stale — retrying into a 409 would achieve nothing.
    const chatsRefetchType = isConnectedRef.current ? undefined : ('none' as const);

    // Guarded so a flush re-armed for a send in flight does not refresh the
    // list a second time — the deferral is about the thread only.
    if (wantsChatsRef.current) {
      wantsChatsRef.current = false;
      void queryClient.invalidateQueries({
        queryKey: ['teams', 'chats'],
        refetchType: chatsRefetchType,
      });
    }

    const chatId = activeChatIdRef.current;
    if (!wantsMessagesRef.current || !chatId) return;

    // Hold the thread invalidation back until the send settles rather than
    // dropping it: the send's own onSettled invalidation refetches the same
    // key, so waiting costs nothing and losing it could leave the thread a
    // message behind. Re-arming keeps the "one refetch per burst" property.
    if (isSendingRef.current && isViewingRef.current) {
      timerRef.current = setTimeout(flush, ACTIVITY_DEBOUNCE_MS);
      return;
    }

    wantsMessagesRef.current = false;
    void queryClient.invalidateQueries({
      queryKey: ['teams', 'chat-messages', chatId],
      refetchType,
    });
  }, [queryClient]);

  useEffect(() => {
    if (!socket) return;

    const handleActivity = (event: TeamsActivityEvent) => {
      // `chatId` is advisory — absent when Graph did not name a thread, in
      // which case the open thread might well be the one that changed, so
      // refetch it. When it is present and names a different chat, only the
      // list ordering/preview can have moved.
      wantsChatsRef.current = true;
      if (!event.chatId || event.chatId === activeChatIdRef.current) {
        wantsMessagesRef.current = true;
      }

      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(flush, ACTIVITY_DEBOUNCE_MS);
    };

    // Notifications sent while the socket was down are simply lost — Graph does
    // not replay them to us, and with the slow poll in place the panel could
    // otherwise sit minutes behind. Treat a *re*connect as "you missed
    // something" and refresh whatever is on screen. Skipped on the first
    // connect, where the queries are mounting and fetching anyway.
    const handleReconnect = () => handleActivity({ type: 'teams:activity', timestamp: '' });

    socket.on(SOCKET_EVENTS.TEAMS_ACTIVITY, handleActivity);
    socket.io.on('reconnect', handleReconnect);
    return () => {
      socket.off(SOCKET_EVENTS.TEAMS_ACTIVITY, handleActivity);
      socket.io.off('reconnect', handleReconnect);
      // The panel is mounted outside the router outlet, so this only runs on
      // logout or socket replacement — but a timer surviving either would fire
      // against a dead query client.
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [socket, flush]);
}
